<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" @cancel="closeModal()">
        <h1>{{ $t('tourMap.tourDetailsModal.title') }}</h1>
      </modal-header>
    </template>
    <BusyArea :busy="loading" style="margin-top: 50px">
      <div v-if="tour" class="grid grid-cols-2 gap-y-4 gap-x-10 mt-4">
        <div class="flex flex-col col-span-2">
          <span class="text-sm font-bold">{{ $t('tourMap.tourDetailsModal.campaign') }}</span>
          <span>{{ tour?.campaignRegistration?.campaign?.name }}</span>
        </div>
        <div class="flex flex-col">
          <span class="text-sm font-bold">{{ $t('tourMap.tourDetailsModal.driver') }}</span>
          <router-link :to="{ name: 'driver-details', params: { driverId: tour?.driver?.id } }">
            {{ tour?.driver.user.firstName }} {{ tour?.driver.user.lastName }}
          </router-link>
        </div>
        <div class="flex flex-col">
          <span class="text-sm font-bold">{{ $t('tourMap.tourDetailsModal.email') }}</span>
          <span>{{ tour?.driver?.user?.email }}</span>
        </div>
        <div class="flex flex-col">
          <span class="text-sm font-bold">{{ $t('tourMap.tourDetailsModal.distance') }}</span>
          <span>{{ (tour.distance / 1000).toFixed(2) }} km</span>
        </div>
        <div class="flex flex-col">
          <span class="text-sm font-bold">{{ $t('tourMap.tourDetailsModal.duration') }}</span>
          <span>{{ formatTimeDurationFromSeconds(tour?.time) }}</span>
        </div>
      </div>
    </BusyArea>
    <div v-if="tour" class="mt-8">
      <CustomButton variant="primary" @click="openTour">
        {{ $t('tourMap.tourDetailsModal.showDetails') }}
      </CustomButton>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Modal from '@utils/Modal/Modal.vue';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { useFetchAction } from '@hooks/fetch';
import { RouterLink, useRouter } from 'vue-router';
import CustomButton from '@utils/CustomButton.vue';
import BusyArea from '@utils/busy/BusyArea.vue';
import type {
  Tour,
} from '@/entities/Entities';
import { formatTimeDurationFromSeconds } from '@/utils/helpers';

interface Props {
  open: boolean;
  tourId: string | null;
}

const emit = defineEmits(['update:open']);
const props = defineProps<Props>();
const router = useRouter();
const internalOpen = useVModel(props, 'open', emit);
const { t } = useI18n();
const [exec, { loading }] = useFetchAction();
const tourId = computed(() => props.tourId);

const tour = ref<Tour|null>(null);

const tourLink = computed(() => ({
  name: 'driver-tours',
  params: {
    driverId: tour.value?.driver?.id,
  },
  query: {
    filter: encodeURIComponent(JSON.stringify({
      id: tour.value?.id,
    })),
  },
}));

const openTour = () => {
  const link = router.resolve(tourLink.value);
  window.open(link.href, '_blank');
};

watch(tourId, async (id) => {
  if (id) {
    tour.value = await exec<Tour>(`/tours/${id}`);
  }
});

function closeModal() {
  internalOpen.value = false;
  tour.value = null;
}
</script>

<style lang="scss" scoped></style>
